import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, STATUS_META } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export const NotificationsBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setItems(data || []);
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    // Realtime: la campana se actualiza al instante ante nuevas notificaciones
    const ch = supabase
      .channel("bell-notifications")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications" }, () => load())
      .subscribe();
    return () => { clearInterval(t); supabase.removeChannel(ch); };
  }, [load]);

  const unread = items.filter((i) => !i.is_read).length;

  // Traduce notificaciones antiguas que guardaban el código crudo del estado
  const translate = (body) => {
    if (!body) return body;
    const m = body.match(/^Nuevo estado: ([A-Z_]+)$/);
    if (m && STATUS_META[m[1]]) return STATUS_META[m[1]].label + ".";
    return body;
  };

  const markAll = async () => {
    await supabase.rpc("mark_all_notifications_read");
    load();
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && unread && markAll()}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative grid h-10 w-10 place-items-center rounded-xl border border-[#27272A] bg-[#18181B] text-gray-300 transition-colors hover:border-[#FF6A00]/50"
          data-testid="notifications-bell"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#FF6A00] px-1 text-[10px] font-extrabold text-black"
              data-testid="notifications-unread-count"
            >
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 border-[#27272A] bg-[#111113] p-0 text-white"
      >
        <div className="border-b border-[#27272A] px-4 py-3 font-heading font-bold">Notificaciones</div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">Sin notificaciones</div>
          )}
          {items.map((n) => (
            <div
              key={n.id}
              onClick={() => n.link && navigate(n.link)}
              className={`border-b border-[#1e1e21] px-4 py-3 ${n.is_read ? "opacity-60" : ""} ${n.link ? "cursor-pointer transition-colors hover:bg-[#18181B]" : ""}`}
              data-testid="notification-item"
            >
              <div className="text-sm font-semibold text-white">{n.title}</div>
              {n.body && <div className="text-xs text-gray-400">{translate(n.body)}</div>}
              <div className="mt-1 text-[10px] text-gray-600">{formatDateTime(n.created_at)}</div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="p-2">
            <Button
              variant="ghost"
              className="w-full text-xs text-gray-400 hover:text-white"
              onClick={markAll}
              data-testid="notifications-mark-all"
            >
              Marcar todas como leídas
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
